import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Landmark, CheckCircle2, AlertTriangle, Loader2, Lock, Pencil } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/**
 * Bank deposit confirmation — gate before paying agencies.
 * KR must confirm the carrier's deposit landed in the bank (expected = sum of
 * Amount Received by KR) before agency statements can be generated.
 */
export function BankDepositCard({ importId, onConfirmed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ deposit_date: '', deposit_amount: '', deposit_note: '' });

  const load = useCallback(async () => {
    if (!importId) return;
    setLoading(true);
    try {
      const { data } = await accountingAPI.getDeposit(importId);
      setData(data);
      setForm({
        deposit_date: data.deposit_date || '',
        deposit_amount: data.deposit_amount != null ? String(data.deposit_amount) : (data.expected_amount != null ? String(data.expected_amount) : ''),
        deposit_note: data.deposit_note || '',
      });
      setEditing(!data.deposit_confirmed);
    } finally { setLoading(false); }
  }, [importId]);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    if (form.deposit_amount === '' || isNaN(Number(form.deposit_amount))) {
      toast.error('Enter the deposited amount'); return;
    }
    setSaving(true);
    try {
      const { data } = await accountingAPI.setDeposit(importId, {
        deposit_confirmed: true,
        deposit_amount: Number(form.deposit_amount),
        deposit_date: form.deposit_date || null,
        deposit_note: form.deposit_note || null,
      });
      setData(data); setEditing(false);
      toast.success('Deposit confirmed — agency payment unlocked');
      onConfirmed?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm deposit');
    } finally { setSaving(false); }
  };

  const unconfirm = async () => {
    setSaving(true);
    try {
      const { data } = await accountingAPI.setDeposit(importId, { deposit_confirmed: false });
      setData(data); setEditing(true);
      toast.message('Deposit confirmation removed');
    } finally { setSaving(false); }
  };

  if (loading || !data) {
    return (
      <Card data-testid="bank-deposit-card"><CardContent className="py-6 text-center text-slate-400">
        <Loader2 className="mx-auto animate-spin" size={18} />
      </CardContent></Card>
    );
  }

  const variance = data.variance;
  const hasVariance = data.status === 'variance';

  return (
    <Card className={data.deposit_confirmed ? (hasVariance ? 'border-amber-300' : 'border-emerald-300') : 'border-slate-200'} data-testid="bank-deposit-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Landmark size={16} /> Bank deposit
          {data.deposit_confirmed ? (
            hasVariance
              ? <Badge className="bg-amber-100 text-amber-800" data-testid="deposit-status-badge">Confirmed · variance</Badge>
              : <Badge className="bg-emerald-100 text-emerald-800" data-testid="deposit-status-badge">Confirmed</Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-700" data-testid="deposit-status-badge"><Lock size={11} className="mr-1" />Pending — blocks agency payment</Badge>
          )}
        </CardTitle>
        <p className="text-xs text-slate-500">
          Confirm the carrier deposit hit your bank before paying agencies.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[11px] text-slate-400">Expected (received by KR)</div>
            <div className="font-semibold" data-testid="deposit-expected">{money(data.expected_amount)}</div>
          </div>
          <div>
            <div className="text-[11px] text-slate-400">Deposited</div>
            <div className="font-semibold">{money(data.deposit_amount)}</div>
          </div>
        </div>

        {data.deposit_confirmed && variance != null && Math.abs(variance) >= 0.01 && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded px-2 py-1.5" data-testid="deposit-variance">
            <AlertTriangle size={14} /> Difference of {money(Math.abs(variance))} ({variance > 0 ? 'over' : 'short'}) vs expected
          </div>
        )}

        {editing ? (
          <div className="space-y-2 pt-1">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Deposit date</Label>
                <Input type="date" value={form.deposit_date}
                  onChange={(e) => setForm((f) => ({ ...f, deposit_date: e.target.value }))}
                  data-testid="deposit-date-input" />
              </div>
              <div>
                <Label className="text-xs">Amount received</Label>
                <Input type="number" step="0.01" value={form.deposit_amount}
                  onChange={(e) => setForm((f) => ({ ...f, deposit_amount: e.target.value }))}
                  data-testid="deposit-amount-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input value={form.deposit_note}
                onChange={(e) => setForm((f) => ({ ...f, deposit_note: e.target.value }))}
                placeholder="e.g. ACH ref, split deposit…" data-testid="deposit-note-input" />
            </div>
            <Button size="sm" onClick={confirm} disabled={saving} data-testid="confirm-deposit-btn">
              {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
              Confirm deposit
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-slate-500">
              {data.deposit_date && <>Deposited {data.deposit_date}. </>}
              {data.deposit_note && <span className="italic">{data.deposit_note}</span>}
            </div>
            <Button size="sm" variant="outline" onClick={unconfirm} disabled={saving} data-testid="edit-deposit-btn">
              <Pencil size={13} className="mr-1" /> Edit
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default BankDepositCard;
